import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { PAGE_LAYOUT } from '~/lib/constants';

import {
  ChakraBox,
  ChakraFlex,
  ChakraTabList,
  ChakraTabs,
} from '@equip.health/ui';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { Path } from '~/routes/constants';
import { useGetPatientQuery } from '~/store/services/patients';
import { isValidUUID } from '~/lib/utils';
import { usePatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import usePatientTaskCount from '~/lib/hooks/usePatientTaskCount';
import useUnsavedChangesGuard from '~/lib/hooks/useUnsavedChangesGuard';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useComponentPortalContext } from '~/lib/contexts/ComponentPortalContext';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import AnalyticsEvents from '~/analytics';
import { PatientCalendarEvents } from '~/analytics/patientsCalendarEvents';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import {
  iframeContainerTabs,
  PatientTabs,
  PatientTabsDefinition,
} from '~/lib/constants/patients.constants';
import {
  AssessmentActionsProvider,
  useAssessmentActionsContext,
} from '~/lib/contexts/AssessmentActionsContext';
import { EditNoteDrawerPortal } from '~/components/common/EditNoteDrawerPortal';
import PatientHeader from './patient-header';
import PatientLayoutTab from './PatientLayoutTab';
import Loading from '~/pages/loading';
import PageErrorState from '~/components/common/PageErrorState';

const tabsWithNoRightPadding = new Set([
  PatientTabs.ASSESSMENTS.toString(),
  PatientTabs.APPOINTMENTS.toString(),
  PatientTabs.ACTIVITY.toString(),
  PatientTabs.NOTES.toString(),
]);

const PatientHeaderWithContext = ({
  patient,
  patientId,
  patientPreferredFullName,
}: {
  patient: Patient;
  patientId: string;
  patientPreferredFullName: string;
}) => {
  const { onContinueSurvey, updateAssessmentState } =
    useAssessmentActionsContext();

  return (
    <PatientHeader
      onContinueSurvey={onContinueSurvey}
      patient={patient}
      patientId={patientId}
      patientPreferredFullName={patientPreferredFullName}
      updateAssessmentState={updateAssessmentState}
    />
  );
};

interface PatientLayoutProps {
  children: ReactNode;
}

const PatientLayout = ({ children }: PatientLayoutProps) => {
  const { patientId } = useParams<{ patientId: string }>();
  const isInvalidPatientId = !patientId || !isValidUUID(patientId);
  const history = useHistory();
  const location = useLocation();
  const { currentUser, setIsOnPatientProfile } = useGlobalContext();

  const {
    data: patient,
    error,
    isFetching,
    isLoading,
    isUninitialized,
    refetch,
  } = useGetPatientQuery({ patientId }, { skip: isInvalidPatientId });
  const { totalCount: patientTasksCount } = usePatientTaskCount(patientId);

  const { patientHasUnsavedChanges } = useComponentPortalContext();

  const shouldBlock = useCallback(
    (destinationPath: string) => {
      const [, , nextRoute, nextPatientId] = destinationPath.split('/');
      return !(nextRoute === 'patients' && nextPatientId === patientId);
    },
    [patientId],
  );

  const { UnsavedChangesModal } = useUnsavedChangesGuard({
    isDirty: patientHasUnsavedChanges,
    shouldBlock,
  });

  const tabsCollection = usePatientTabsCollection(
    currentUser?.providerTypeDetails?.name,
  );
  const tabs = useMemo(() => Object.entries(tabsCollection), [tabsCollection]);

  const pathAfterPatientId =
    location.pathname.split(`${patientId}/`)[1]?.split('/')[0] || '';

  const contentPaddingRight = tabsWithNoRightPadding.has(pathAfterPatientId)
    ? '0'
    : PAGE_LAYOUT.rightSpacing;

  const shouldDisableOuterScroll = iframeContainerTabs.has(pathAfterPatientId);

  const tabIndex = useMemo(() => {
    const index = tabs.findIndex(([, { subPath }]) =>
      pathAfterPatientId.startsWith(subPath),
    );
    return index >= 0 ? index : -1;
  }, [tabs, pathAfterPatientId]);

  const { trackEvent } = useAnalytics();

  const trackTabChange = (subPath: string) => {
    if (subPath === 'appointments') {
      trackEvent(PatientCalendarEvents.dailyViewTabButtonClicked, {
        patientId,
        Source: AnalyticsSourceValues.PATIENT_CALENDAR_TAB,
      });
    }
    if (subPath === 'messages') {
      trackEvent(AnalyticsEvents.patientChartMessagesTab, {
        patientId,
        source: AnalyticsSourceValues.PATIENT_CHART_NAVIGATION,
      });
    }
    if (subPath === 'care-planning') {
      trackEvent(CarePlanEvents.clickOnCarePlanActivityTab, {
        patientId,
        source: AnalyticsSourceValues.CARE_PLAN_TAB,
      });
    }
    if (subPath === 'journal') {
      trackEvent(AnalyticsEvents.journalTabButtonClicked, {
        patientId,
        source: AnalyticsSourceValues.SMT_TAB,
      });
    }
  };

  const handleTabChange = (index: number) => {
    const [, tab] = tabs[index];
    if (tab) {
      trackTabChange(tab.subPath);
      history.push(`${Path.MAUD_PATIENTS}/${patientId}/${tab.subPath}`);
    }
  };

  useEffect(() => {
    setIsOnPatientProfile(!!patient?.userId);
    return () => {
      setIsOnPatientProfile(false);
    };
  }, [patient?.userId]);

  // useGetPatientQuery returns a plain-text 404 body that fails JSON parsing, e.g. "Patient not found with id: <uuid>"
  // In this case, RTK Query wraps it as a { status: PARSING_ERROR, originalStatus: <HTTP code> }
  const is404 =
    error &&
    (('status' in error && error.status === 404) ||
      ('originalStatus' in error && error.originalStatus === 404));
  const isRetryableError = error && !is404;

  const pageNotFound = (
    <PageErrorState
      description="Please check that the URL entered is correct."
      heading="Page not found"
    />
  );

  if (isInvalidPatientId) return pageNotFound;
  if (isLoading || isUninitialized) return <Loading />;
  if (isRetryableError)
    return <PageErrorState isRetrying={isFetching} onRetry={refetch} />;
  if (!patient) return pageNotFound;

  const patientPreferredFullName = `${patient.chosenName || patient.firstName} ${patient.lastName}`;

  return (
    <AssessmentActionsProvider patientId={patientId}>
      {UnsavedChangesModal}
      <ChakraFlex alignItems="stretch" direction="column" height="100vh">
        <ChakraBox flex="0 0 auto" paddingRight="24px">
          <PatientHeaderWithContext
            patient={patient}
            patientId={patientId}
            patientPreferredFullName={patientPreferredFullName}
          />
          <ChakraTabs
            index={tabIndex}
            onChange={handleTabChange}
            variant="line"
          >
            <ChakraTabList
              borderBottom="1px solid"
              borderBottomColor="neutral.line"
              columnGap="10px"
              flexWrap="wrap"
              rowGap="2px"
            >
              {tabs.map(
                ([tab, { label, subPath }]: [
                  string,
                  PatientTabsDefinition,
                ]) => (
                  <PatientLayoutTab
                    isActive={pathAfterPatientId === subPath}
                    key={subPath}
                    label={label}
                    subPath={subPath}
                    tab={tab}
                    taskCount={patientTasksCount}
                  />
                ),
              )}
            </ChakraTabList>
          </ChakraTabs>
        </ChakraBox>
        <ChakraBox
          flex={1}
          overflowX="hidden"
          overflowY={shouldDisableOuterScroll ? 'hidden' : 'auto'}
          paddingRight={contentPaddingRight}
          paddingTop="12px"
          width="100%"
        >
          {children}
        </ChakraBox>
        <EditNoteDrawerPortal />
      </ChakraFlex>
    </AssessmentActionsProvider>
  );
};

export default PatientLayout;
